import { supabase } from '../lib/supabase';
import { AssistantTemplate } from '../types/assistant';

export class AssistantTemplatesService {
  // Buscar todos os templates
  async getAllTemplates(): Promise<AssistantTemplate[]> {
    const { data, error } = await supabase
      .from('assistant_templates')
      .select('*')
      .order('is_featured', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar templates:', error);
      throw error;
    }

    return (data || []).map(this.mapTemplateFromDB);
  }

  // Buscar templates por categoria
  async getTemplatesByCategory(category: string): Promise<AssistantTemplate[]> {
    const { data, error } = await supabase
      .from('assistant_templates')
      .select('*')
      .eq('category', category)
      .order('is_featured', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar templates por categoria:', error);
      throw error;
    }

    return (data || []).map(this.mapTemplateFromDB);
  }

  // Buscar templates em destaque
  async getFeaturedTemplates(): Promise<AssistantTemplate[]> {
    const { data, error } = await supabase
      .from('assistant_templates')
      .select('*')
      .eq('is_featured', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar templates em destaque:', error);
      throw error;
    }

    return (data || []).map(this.mapTemplateFromDB);
  }

  // Criar novo template (admin only)
  async createTemplate(template: Omit<AssistantTemplate, 'id'>): Promise<AssistantTemplate> {
    const { data, error } = await supabase
      .from('assistant_templates')
      .insert({
        name: template.name,
        description: template.description,
        instructions: template.instructions,
        avatar: template.avatar,
        category: template.category,
        ai_provider: template.aiProvider,
        model_id: template.modelId,
        is_featured: template.isFeatured || false,
        tags: template.tags || [],
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar template:', error);
      throw error;
    }

    return this.mapTemplateFromDB(data);
  }

  // Atualizar template (admin only)
  async updateTemplate(id: string, updates: Partial<AssistantTemplate>): Promise<AssistantTemplate> {
    const updateData: any = {};

    if (updates.name) updateData.name = updates.name;
    if (updates.description) updateData.description = updates.description;
    if (updates.instructions) updateData.instructions = updates.instructions;
    if (updates.avatar) updateData.avatar = updates.avatar;
    if (updates.category) updateData.category = updates.category;
    if (updates.aiProvider) updateData.ai_provider = updates.aiProvider;
    if (updates.modelId) updateData.model_id = updates.modelId;
    if (updates.isFeatured !== undefined) updateData.is_featured = updates.isFeatured;
    if (updates.tags) updateData.tags = updates.tags;

    const { data, error } = await supabase
      .from('assistant_templates')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar template:', error);
      throw error;
    }

    return this.mapTemplateFromDB(data);
  }

  // Deletar template (admin only)
  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from('assistant_templates')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao deletar template:', error);
      throw error;
    }
  }

  // Toggle featured status
  async toggleFeatured(id: string, isFeatured: boolean): Promise<void> {
    const { error } = await supabase
      .from('assistant_templates')
      .update({ is_featured: isFeatured })
      .eq('id', id);

    if (error) {
      console.error('Erro ao atualizar status featured:', error);
      throw error;
    }
  }

  // Mapear dados do banco para o tipo TypeScript
  private mapTemplateFromDB(dbTemplate: any): AssistantTemplate {
    return {
      id: dbTemplate.id,
      name: dbTemplate.name,
      description: dbTemplate.description,
      instructions: dbTemplate.instructions,
      avatar: dbTemplate.avatar,
      category: dbTemplate.category,
      aiProvider: dbTemplate.ai_provider,
      modelId: dbTemplate.model_id,
      isFeatured: dbTemplate.is_featured,
      tags: dbTemplate.tags || [],
    };
  }
}

export const assistantTemplatesService = new AssistantTemplatesService();

